import { ItemAvailabilityChangeContract } from 'interfaces'
import { Dictionary } from 'utilities/typeUtility/typeUtility'
import {
    values,
    cloneDeep,
    keyBy,
    keys,
    groupBy,
    isEmpty,
    sortBy,
} from 'lodash-es'
import {
    AvailabilityContract,
    ComboChoiceContract,
    ComboChoiceItemContract,
    ComboChoiceItemVariantOptionContract,
    ItemCategoryLiteContract,
    ItemModifierContract,
    MenuContract,
    MenuItemContract,
    ModifierGroupContract,
    ModifierGroupOptionType,
    ModifierOptionContract,
    POSCombinedModel,
    POSPriceListContract,
    POSVariantGroup,
    PosTerminalMenuSlimContract,
    ProductContract,
    UnavailableItemsContract,
} from 'models'
import {
    MenuViewModel,
    ItemViewModel,
    MenuSectionViewModel,
    ComboViewModel,
    PriceListData,
    convertToComboViewModel,
    ComboItemViewmodel,
    EntityModifierViewModel,
    createEntityModifierViewModel,
    POSVariantOptionWithPrice,
    IOnlineStoreWithBrand,
    convertToItemViewModel,
    CategoryViewModel,
} from 'store/models/sellModels'
import Guid from 'utilities/Guid'
import { AddComboPriceToBasePriceListItemData } from 'utilities/price/getPrice'
import { UnavailableItemType } from 'models/UnavailableItemType'
export const MENUSECTION_ALL_ITEMS = Guid.empty
export const ALL_ITEMS_CATEGORY_ID = Guid.empty
const ALL_ITEMS = 'All Items'

export const defaultAllCategory: () => CategoryViewModel = () => ({
    categoryId: ALL_ITEMS_CATEGORY_ID,
    name: ALL_ITEMS,
})

export const updateUnavailableItems = (
    unavailableProducts: UnavailableItemsContract,
) => (items: ItemAvailabilityChangeContract[]) => {
    const updateUnavailableProducts: UnavailableItemsContract = unavailableProducts
    let unavailableStockItems =
        updateUnavailableProducts.unavailableStockItems ?? []
    let unavailableSalesItems =
        updateUnavailableProducts.unavailableSalesItems ?? []
    items.forEach((item) => {
        if (!item.isAvailableToSelect) {
            // Insert to unavailable sales items
            if (unavailableSalesItems.every((i) => i.itemID !== item.itemID))
                unavailableSalesItems.push({
                    itemID: item.itemID,
                    unavailableItemType: UnavailableItemType.Unavailable,
                    unavailableReason:
                        'products.unavailable_products.message.unavailable.item',
                })
        } else if (item.isOutOfStock) {
            if (unavailableStockItems.every((i) => i.itemID !== item.itemID)) {
                unavailableStockItems.push({
                    itemID: item.itemID,
                    unavailableItemType: UnavailableItemType.OutOfStock,
                    unavailableReason:
                        'products.unavailable_products.message.out_of_stock.item',
                })
            }
        } else {
            // Remove from unavailable sales items
            unavailableSalesItems =
                unavailableSalesItems.filter((i) => i.itemID !== item.itemID) ??
                []

            // Remove from unavailable stock items
            unavailableStockItems =
                unavailableStockItems.filter((i) => i.itemID !== item.itemID) ??
                []
        }
    })

    return {
        unavailableStockItems,
        unavailableSalesItems,
    }
}

function notHasItemOrHasMenu(
    mi: MenuItemContract,
    dMenu: { [key: string]: MenuViewModel },
    dItem: { [key: string]: ItemViewModel },
) {
    return !mi.menuID || !dMenu[mi.menuID] || !mi.itemID || !dItem[mi.itemID]
}
export function applyMenuSectionToItem(
    menu: MenuContract[],
    dItem: { [key: string]: ItemViewModel },
    dMenu: { [key: string]: MenuViewModel },
) {
    const allItems = values(dItem)

    const menuItems = menu.flatMap((a) => a.menuItems ?? [])

    menuItems.forEach((mi) => {
        const baseItem = allItems.find(
            (a) =>
                a.variations.some((v) => v.itemId === mi.itemID) ||
                a.depositAndCreditItems.some((i) => i.itemId === mi.itemID),
        )

        if (notHasItemOrHasMenu(mi, dMenu, dItem) && !baseItem) return

        if (baseItem) {
            baseItem.inMenuSections[mi.menuSectionID!] =
                mi.displayOrder === 0 ? -1 : mi.displayOrder
        } else
            dItem[mi.itemID!].inMenuSections[mi.menuSectionID!] =
                mi.displayOrder === 0 ? -1 : mi.displayOrder
    })
}

export function createMenus(
    menuItems: MenuItemContract[],
    dMenu: { [key: string]: MenuViewModel },
    dItem: { [key: string]: ItemViewModel },
    dCategories: { [key: string]: CategoryViewModel },
) {
    const allItems = values(dItem)
    //menu item always not include base item but allItems only has not variant product
    menuItems.forEach((mi) => {
        const baseItem = allItems.find(
            (i) =>
                i.variations.some((v) => v.itemId === mi.itemID) ||
                i.depositAndCreditItems.some((d) => d.itemId === mi.itemID),
        )
        if (notHasItemOrHasMenu(mi, dMenu, dItem) && !baseItem) return
        const menuItem = baseItem || dItem[mi.itemID || '']!

        const menu = dMenu[mi.menuID!]

        if (!menu) {
            console.error(`Menu ${mi.menuID} not found`)
            return
        }

        menu.menuItemDetailMap[mi.menuItemID!] = {
            itemID: mi.itemID!,
            baseItemID: baseItem?.itemId,
            menuSectionID: mi.menuSectionID!,
            isAvailable: mi?.isAvailable!,
            availabilities: mi.availabilities ?? [],
            menuItemModifierOptions: mi.menuItemModifierOptions ?? [],
        }

        if (!menu.items.some((a) => a.itemId === menuItem.itemId))
            menu.items.push(menuItem)
        // Add items to category in menu
        if (!menu.categoryMap[menuItem.categoryId]) {
            const newCategory: CategoryViewModel = {
                categoryId: menuItem.categoryId,
                name: dCategories[menuItem.categoryId]?.name ?? '',
            }
            menu.categoryMap[newCategory.categoryId] = newCategory
        }
        menu.categoryMap[ALL_ITEMS_CATEGORY_ID] = defaultAllCategory()
        menu.categories = values(menu.categoryMap)
    })

    let menus = values(dMenu)

    const isNotEmptyCategory = (
        c: CategoryViewModel,
        items: ItemViewModel[],
    ) => {
        return items.some((a) => a.categoryId === c.categoryId)
    }
    const isNotEmptyMenu = (m: MenuViewModel) => {
        return m.items.length > 0
    }
    const isNotEmptyMenuSection = (menuSection: MenuSectionViewModel) => {
        return values(dItem).some(
            (i) => i.inMenuSections[menuSection.menuSectionId],
        )
    }

    // Remove empty categories.
    menus.forEach((m) => {
        m.categories = m.categories.filter(
            (c) =>
                isNotEmptyCategory(c, m.items) ||
                c.categoryId === ALL_ITEMS_CATEGORY_ID,
        )

        m.categoryMap = keyBy(m.categories, (a) => a.categoryId)
        const all_cat = m.categoryMap[ALL_ITEMS_CATEGORY_ID]
        const sort_cat = m.categories
            .filter((i) => i.categoryId !== ALL_ITEMS_CATEGORY_ID)
            .sort((c1, c2) => c1.name.localeCompare(c2.name))
        m.categories = [all_cat, ...sort_cat]
    })

    // Remove empty menus.
    menus = menus.filter(isNotEmptyMenu)

    // Remove empty menu sections.
    menus.forEach((menu) => {
        menu.menuSections = menu.menuSections.filter((section) => {
            if (section.menuSectionId === MENUSECTION_ALL_ITEMS) return true
            section.subMenuSections = section.subMenuSections.filter(
                isNotEmptyMenuSection,
            )
            return (
                section.subMenuSections.length > 0 ||
                isNotEmptyMenuSection(section)
            )
        })
    })

    // Sort menus by putting non-user-defined menus (e.g. the "All" menu) first.
    return menus.sort((a, b) => {
        return a.isUserDefinedMenu === b.isUserDefinedMenu
            ? 0
            : a.isUserDefinedMenu
            ? 1
            : -1
    })
}

export function prepareAndAddOptionToComboChoicesMap(
    comboChoicesMap: { [key: string]: ComboViewModel[] },
    comboChoice: ComboChoiceContract,
    dItem: { [key: string]: ItemViewModel },
    variantGroupsMap: { [key: string]: POSVariantGroup },
    priceListData: PriceListData,
) {
    if (!dItem[comboChoice.itemID ?? '']) return
    if (!comboChoicesMap[comboChoice.itemID! ?? ''])
        comboChoicesMap[comboChoice.itemID ?? ''] = []
    let hasAdditionalPrice =
        dItem[comboChoice.itemID!].hasAdditionalPrice ?? false
    const comboViewmodel: ComboViewModel = convertToComboViewModel(comboChoice)

    comboChoice.comboChoiceItems?.forEach((comboChoiceItem) => {
        const {
            itemID,
            locationComboChoiceItems,
            comboChoiceItemID,
            comboChoiceItemVariantOptions,
        } = comboChoiceItem
        if (!dItem[itemID!!]) return
        const baseItem = cloneDeep(dItem[itemID!!])
        const priceAdd = locationComboChoiceItems?.[0]?.priceAdd ?? 0
        if (priceAdd > 0) hasAdditionalPrice = true
        AddComboPriceToBasePriceListItemData(
            priceListData,
            comboChoiceItem.comboChoiceItemID ?? '',
            priceAdd,
        )
        const variations = getVariantionItemForComboChoice(
            baseItem,
            comboChoiceItem,
        )
        const comboItem: ComboItemViewmodel = {
            comboChoiceItemId: comboChoiceItemID!!,
            itemID: itemID!!,
            addPrice: priceAdd,
            item: { ...baseItem, variations },
            variantGroups: [],
        }
        addVariantOptionsToComboVariantGroupsMap(
            comboChoiceItemVariantOptions ?? [],
            baseItem,
            comboItem,
            variantGroupsMap,
        )
        comboItem.item.isAvailableForSelection =
            // Not available if some variant group unable to select at least 1 option
            !comboItem.variantGroups.some(
                (vg) => !vg.variantOptions.some((op) => op.isAvailable),
            ) && comboItem.item.isAvailableForSelection
        comboViewmodel.comboItems.push(comboItem)
        hasAdditionalPrice =
            hasAdditionalPrice ||
            comboItem.variantGroups.some((vg) =>
                vg.variantOptions.some((op) => op.addPrice > 0),
            )
    })
    dItem[comboChoice.itemID!].hasAdditionalPrice = hasAdditionalPrice
    comboChoicesMap[comboChoice.itemID ?? ''].push(comboViewmodel)
}

export function bindingEntityToModifierGroup(
    modifierGroupsMap: Dictionary<ModifierGroupContract>,
    posData: POSCombinedModel,
) {
    const entityModifiersMap: { [key: string]: EntityModifierViewModel } = {}
    const addToBindingEntityModifierDict = (
        i: ItemModifierContract,
        id: string,
        entityId: string,
    ) => {
        entityModifiersMap[id] = createEntityModifierViewModel(
            i,
            id,
            entityId,
            modifierGroupsMap,
        )
    }

    posData.itemModifiers?.forEach((i) => {
        addToBindingEntityModifierDict(i, i.itemModifierID!!, i.itemID!!)
    })
    posData.itemCategoryModifiers?.forEach((i) => {
        addToBindingEntityModifierDict(
            i,
            i.itemCategoryModifierID!!,
            i.itemCategoryID!!,
        )
    })
    posData.variantOptionModifiers?.forEach((i) => {
        addToBindingEntityModifierDict(
            i,
            i.variantOptionModifierID!!,
            i.variantOptionID!!,
        )
    })
    posData.modifierOptionModifiers?.forEach((i) => {
        addToBindingEntityModifierDict(
            i,
            i.modifierOptionModifierID!!,
            i.modifierOptionID!!,
        )
    })
    return entityModifiersMap
}

export function getSelectedCategoryOrDefault(
    selectedMenu: MenuViewModel | undefined,
    selectedCategoryId: string | undefined,
) {
    return (
        selectedMenu &&
        selectedMenu.categoryMap[selectedCategoryId || ALL_ITEMS_CATEGORY_ID]
    )
}

export function getSelectedMenuOrDefault(
    menus: MenuViewModel[],
    selectedMenuId: string | undefined,
    terminalMenu: PosTerminalMenuSlimContract,
) {
    let selectedMenu = menus.find((m) => m.menuId === selectedMenuId)
    if (!selectedMenu) {
        if (terminalMenu.defaultMenuId) {
            selectedMenu =
                menus.find((m) => m.menuId === terminalMenu.defaultMenuId) ??
                menus[0]
        }
        //get the All menu to default menu if not setting default menu
        else {
            return menus.find((a) => !a.isUserDefinedMenu) ?? menus[0]
        }
    }

    return selectedMenu
}

function getVariantionItemForComboChoice(
    choiceItem: ItemViewModel,
    comboChoiceItem: ComboChoiceItemContract,
) {
    return choiceItem.variations.filter((variation) => {
        return variation.combinationIds?.every((combiId) =>
            comboChoiceItem.comboChoiceItemVariantOptions?.some(
                (a) => a.variantOptionID === combiId,
            ),
        )
    })
}

export function addVariantOptionsToComboVariantGroupsMap(
    comboVariantOptions: ComboChoiceItemVariantOptionContract[],
    baseItem: ItemViewModel,
    comboItem: ComboItemViewmodel,
    variantGroupsMap: { [key: string]: POSVariantGroup },
) {
    const comboVariantGroupsMap = groupBy(comboVariantOptions, 'variantGroupID')
    const itemVariantGroups = keyBy(
        baseItem.variantGroups,
        (v) => v.variantGroupID!,
    )
    Object.keys(comboVariantGroupsMap).forEach((vgId) => {
        const optionViewModels =
            comboVariantGroupsMap[vgId]?.map<POSVariantOptionWithPrice>(
                (comboVariantGroup) => {
                    const comboVariantOptions =
                        itemVariantGroups[vgId]?.variantOptions?.find(
                            (a) =>
                                a.variantOptionID ===
                                comboVariantGroup.variantOptionID,
                        ) ?? {}
                    return {
                        ...comboVariantOptions,
                        isDefaultOption:
                            comboVariantGroup?.isDefaultOption ?? false,
                        addPrice:
                            comboVariantGroup
                                ?.locationComboChoiceItemVariantOptions?.[0]
                                ?.priceAdd ?? 0,
                    }
                },
            ) ?? []
        comboItem.variantGroups.push({
            ...variantGroupsMap[vgId],
            variantOptions: optionViewModels,
        })
    })
}

export function bindingBrandToMenu(
    menus: MenuViewModel[],
    dItem: { [key: string]: ItemViewModel },
) {
    return menus.map<MenuViewModel>((menu) => {
        let brandID: string = ''
        const itemIDhasBrand = keys(menu.menuItemDetailMap).find(
            (itemId) =>
                itemId.includes(dItem[itemId]?.itemId) &&
                dItem[itemId]?.brandID,
        )
        if (itemIDhasBrand) {
            brandID = dItem[itemIDhasBrand].brandID
        }

        return {
            ...menu,
            brandID,
        }
    })
}

export function createOnlineStoreMenu(
    posData: POSCombinedModel,
    menusWithBrand: MenuViewModel[],
) {
    return posData.onlineStores?.map<IOnlineStoreWithBrand>((onlineStore) => {
        let brandID: string = ''
        if (
            onlineStore.onlineStoreMenus?.some((store) =>
                menusWithBrand.some(
                    (menu) => menu.menuId === store.menuID && menu.brandID,
                ),
            )
        ) {
            brandID =
                {
                    ...menusWithBrand.find(
                        (menu) =>
                            onlineStore.onlineStoreMenus?.some(
                                (store) => store.menuID === menu.menuId!!,
                            ) && menu.brandID,
                    ),
                }.brandID ?? ''
        }

        return {
            ...onlineStore,
            brandID,
        } as IOnlineStoreWithBrand
    })
}

export function createMenuDict(
    menus: MenuContract[],
    terminalMenu: PosTerminalMenuSlimContract,
) {
    const dMenu: { [key: string]: MenuViewModel } = {}
    menus?.forEach((menu) => {
        if (!menu.menuID) return

        // Drop all menus that not belong to current POS Terminal
        if (
            !menu.isUserDefinedMenu ||
            !terminalMenu.menuIds ||
            terminalMenu.menuIds.length === 0 ||
            terminalMenu.menuIds.includes(menu.menuID) ||
            menu.isOnlineMenu // Ignore online menu
        ) {
            const menuSectionAvailabilitiesMap: {
                [menuSectionId: string]: AvailabilityContract[]
            } = {}

            menu.menuSections?.forEach((a) => {
                menuSectionAvailabilitiesMap[a.menuSectionID!] =
                    a.menuSectionAvailabilities?.flatMap(
                        (a) => a.availability ?? [],
                    ) ?? []
            })
            let menuSections =
                menu.menuSections
                    ?.filter((a) => a.isAlreadyApplied!!)
                    ?.map<MenuSectionViewModel>((a) => {
                        return {
                            parentMenuSectionId: a.parentMenuSectionId!,
                            menuSectionId: a.menuSectionID!,
                            name: a.displayName || a.name!,
                            subMenuSections: [],
                            displayOrder: a.displayOrder ?? 0,
                            menuItems: sortBy(
                                a.menuItems ?? [],
                                (c) => c.displayOrder ?? 0,
                            ).map((a) => a.itemID!),
                        }
                    }) ?? []

            //include sub to parent
            menuSections.forEach((a) => {
                a.subMenuSections = menuSections.filter(
                    (b) => b.parentMenuSectionId === a.menuSectionId,
                )
            })
            //filter sub from 1st level
            menuSections = menuSections.filter((a) =>
                isEmpty(a.parentMenuSectionId),
            )

            menuSections.push({
                menuSectionId: MENUSECTION_ALL_ITEMS,
                name: ALL_ITEMS,
                subMenuSections: [],
                displayOrder: -1,
            })

            const menuItemCategoryAvailabilitiesMap: {
                [categoryID: string]: AvailabilityContract[]
            } = {}

            const groupCategoryAvailabilities = groupBy(
                menu.menuItemCategoryAvailabilities,
                (m) => m.itemCategoryID,
            )

            keys(groupCategoryAvailabilities).map(
                (cateID) =>
                    (menuItemCategoryAvailabilitiesMap[
                        cateID
                    ] = groupCategoryAvailabilities[cateID].flatMap(
                        (a) => a.availability!,
                    )),
            )

            dMenu[menu.menuID] = {
                menuId: menu.menuID,
                name: menu.name || '',
                categories: [],
                categoryMap: {},
                defaultBackgroundColor: menu.defaultMenuItemBGColor,
                isUserDefinedMenu: menu.isUserDefinedMenu!!,
                brandID: menu.brandID ?? '',
                isOnlineMenu: menu.isOnlineMenu!!,
                availabilities: menu.availabilities ?? [],
                menuSectionAvailabilitiesMap,
                menuItemCategoryAvailabilitiesMap,
                priceListID: menu.priceListID!,
                menuSections,
                //apply in createMenus
                items: [],
                menuItemDetailMap: {},
            }
            // Attach all category to every Menu
            dMenu[menu.menuID].categoryMap[
                ALL_ITEMS_CATEGORY_ID
            ] = defaultAllCategory()
        }
    })
    return dMenu
}

export function createCategoryDict(categories: ItemCategoryLiteContract[]) {
    const dCategories: { [key: string]: CategoryViewModel } = {}
    categories.forEach((c) => {
        if (!c.itemCategoryID) return

        if (!dCategories[c.itemCategoryID])
            dCategories[c.itemCategoryID] = {
                categoryId: c.itemCategoryID,
                name: c.name!,
            }
    })
    dCategories[ALL_ITEMS_CATEGORY_ID] = defaultAllCategory()
    return dCategories
}

export function createProductDict(
    products: ProductContract[] | null | undefined,
) {
    const dItem: { [key: string]: ItemViewModel } = {}
    products?.forEach((i: ProductContract) => {
        if (!i.itemID || !i.isActive) return

        if (
            !dItem[i.itemID] &&
            !i.baseItemID &&
            !i.baseDepositAndCreditItemID
        ) {
            dItem[i.itemID] = convertToItemViewModel(i)
        }
    })
    return dItem
}

export function prepareAndAddOptionToModifierGroupsMap(
    opt: ModifierOptionContract,
    modifierGroupsMap: Dictionary<ModifierGroupContract>,
    dItem: { [key: string]: ItemViewModel },
) {
    if (opt.modifierGroupID) {
        if (!modifierGroupsMap[opt.modifierGroupID].modifierOptions)
            modifierGroupsMap[opt.modifierGroupID].modifierOptions = []

        //Disable modifier option(item) if item is unavailable
        if (
            modifierGroupsMap[opt.modifierGroupID].optionType ===
                ModifierGroupOptionType.Item &&
            dItem[opt.itemID!!]
        ) {
            opt.locationModifierOptions = [
                {
                    isAvailableForSelection:
                        (opt.locationModifierOptions?.[0]
                            ?.isAvailableForSelection ??
                            true) &&
                        dItem[opt.itemID!!].isAvailableForSelection,
                    // priceAdd: opt.locationModifierOptions?.[0]?.priceAdd ?? 0,
                },
            ]
        }
        modifierGroupsMap[opt.modifierGroupID].modifierOptions!!.push(opt)
    }
}

export function comboChoiceSorter(a: ComboViewModel, b: ComboViewModel) {
    const ret = a.createdOn.localeCompare(b.createdOn)
    if (ret) return ret
    return a.name.localeCompare(b.name)
}

export function createPriceListData(
    priceLists: POSPriceListContract[],
    menus: MenuContract[],
): PriceListData {
    const priceListMaps = keyBy(priceLists, (p) => p.priceListID ?? '')
    const basePriceList = priceLists.find((a) => a.isBasePriceList)!
    const menuPriceListMap: { [key: string]: POSPriceListContract } = {}
    const modifierGroupPriceListMap: {
        [key: string]: POSPriceListContract
    } = {}
    menus.forEach((m) => {
        menuPriceListMap[m.menuID ?? ''] =
            priceListMaps[m.priceListID ?? ''] ?? basePriceList
    })

    return {
        basePriceList,
        menuPriceListMap,
        modifierGroupPriceListMap,
    }
}
export function selectDefaultMenuSection(
    selectedMenu: MenuViewModel,
    selectBrandID: string | undefined,
    menuItems: ItemViewModel[],
): string {
    const menuSections = selectedMenu?.menuSections ?? []
    return (
        sortBy(menuSections, ['displayOrder', 'name'])?.find(
            (f) =>
                f.menuSectionId === MENUSECTION_ALL_ITEMS ||
                menuItems.some(
                    (a) =>
                        a.inMenuSections[f.menuSectionId] &&
                        (!selectBrandID || a.brandID === selectBrandID!!),
                ),
        )?.menuSectionId ?? MENUSECTION_ALL_ITEMS
    )
}
